import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_API_BASE } from '../../../api';
import { Alert } from '../../../ui/atoms';
import { BaseModal } from '../../../ui/atoms/Modal/Modal';
import { formatDisplayDateTime } from '../../../utils/dateFormatting';

/**
 * "AI tools" (MCP): WordPress sites connected to the hosted ProseCaptain MCP, MCP tokens for
 * Claude / Cursor / VS Code, connected plugins and the change log.
 *
 *   hosted MCP  (wp-mcp.prosecaptain.com /app/*)   sites, MCP tokens, events, connect a site
 *   article-queue (/api/workspace/site-tokens)     the plugin's per-site tokens
 *
 * Both are called with the signed-in user's Directus token; each resolves the user's own workspace.
 * A new MCP token is shown once, together with one-click installs (Cursor, VS Code) and the Claude Code command.
 */
export const WP_MCP_BASE = (process.env.REACT_APP_WP_MCP_URL || 'https://wp-mcp.prosecaptain.com').replace(/\/+$/, '');
const DOCS_URL = 'https://prosecaptain.com/dokumentacja/mcp/';

const authHeaders = () => {
  const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
  return { Authorization: `Bearer ${token}` };
};
const mcpApi = (method, path, data) => axios({ method, url: `${WP_MCP_BASE}${path}`, data, headers: authHeaders() }).then((r) => r.data);
const errorCode = (e) => e?.response?.data?.error?.code || e?.response?.data?.error || 'network_error';

const base64 = (s) => window.btoa(unescape(encodeURIComponent(s)));
export const installLinks = (mcpUrl, token) => {
  const headers = { Authorization: `Bearer ${token}` };
  return {
    cursor: `cursor://anysphere.cursor-deeplink/mcp/install?name=prosecaptain-wp&config=${encodeURIComponent(base64(JSON.stringify({ url: mcpUrl, headers })))}`,
    vscode: `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: 'prosecaptain-wp', type: 'http', url: mcpUrl, headers }))}`,
    claudeCode: `claude mcp add --transport http prosecaptain-wp ${mcpUrl} --header "Authorization: Bearer ${token}"`,
  };
};

const card = 'overflow-hidden rounded-lg bg-white shadow ring-1 ring-black/5';
const th = 'px-4 py-3 text-left text-sm font-semibold text-gray-900';
const td = 'px-4 py-3 text-sm text-gray-600';
const btnPrimary = 'inline-flex h-10 items-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50';
const btnGhost = 'inline-flex h-10 items-center gap-2 rounded-md bg-white px-4 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';
const btnDanger = 'text-sm font-semibold text-red-600 hover:text-red-500 disabled:opacity-50';

function CopyField({ value, label, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* select by hand */ }
  };
  return (
    <div>
      {label && <p className="text-sm font-medium text-gray-900">{label}</p>}
      <div className="mt-2 flex gap-2">
        <code className="block min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-gray-900 px-4 py-2 text-[13px] leading-6 text-white">{value}</code>
        <button type="button" onClick={copy} className={btnGhost}>{copied ? t('userProfile.mcp.copied') : t('userProfile.mcp.copy')}</button>
      </div>
    </div>
  );
}

function TermsCheckbox({ terms, checked, onChange, t, id }) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-sm text-gray-700">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600" />
      <span>
        {t('userProfile.mcp.terms.before')}{' '}
        <a href={terms?.url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline">{t('userProfile.mcp.terms.link')}</a>{' '}
        {t('userProfile.mcp.terms.and')}{' '}
        <a href={terms?.privacyUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline">{t('userProfile.mcp.terms.privacy')}</a>
        {terms?.version ? ` (${t('userProfile.mcp.terms.version', { version: terms.version })})` : ''}.
      </span>
    </label>
  );
}

const McpTab = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [tokenForm, setTokenForm] = useState({ open: false, label: '', accept: false });
  const [siteForm, setSiteForm] = useState({ open: false, url: '', env: 'production', accept: false });
  const [issued, setIssued] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fmt = (d) => (d ? formatDisplayDateTime(d, i18n.language) : t('userProfile.mcp.never'));
  const errorText = (code) => t(`userProfile.mcp.errors.${code}`, { defaultValue: t('userProfile.mcp.errors.generic', { code }) });

  const load = useCallback(async () => {
    setError('');
    try {
      const [overview, siteTokens] = await Promise.all([
        mcpApi('get', '/app/overview'),
        axios.get(`${ACCOUNT_API_BASE}/workspace/site-tokens`, { headers: authHeaders() }).then((r) => r.data?.sites || []).catch(() => []),
      ]);
      setData(overview);
      setPlugins(siteTokens);
    } catch (e) {
      setError(errorText(errorCode(e)));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okText) => {
    setBusy(key);
    setNotice(null);
    try {
      await fn();
      if (okText) setNotice({ type: 'success', text: okText });
      await load();
    } catch (e) {
      setNotice({ type: 'error', text: errorText(errorCode(e)) });
    } finally {
      setBusy('');
    }
  };

  const createToken = () => run('token', async () => {
    const res = await mcpApi('post', '/app/tokens', { label: tokenForm.label.trim() || 'MCP', accept_terms: true, terms_version: data.terms.version });
    setIssued(res);
    setTokenForm({ open: false, label: '', accept: false });
  });

  const connectSite = () => run('site', async () => {
    const res = await mcpApi('post', '/app/sites/connect', { url: siteForm.url.trim(), env: siteForm.env, accept_terms: true, terms_version: data.terms.version });
    window.location.href = res.authorize_url;
  });

  const doConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === 'site') return run(`site:${c.id}`, () => mcpApi('delete', `/app/sites/${encodeURIComponent(c.id)}`), t('userProfile.mcp.sites.disconnected', { name: c.id }));
    if (c.kind === 'token') return run(`token:${c.id}`, () => mcpApi('delete', `/app/tokens/${encodeURIComponent(c.id)}`), t('userProfile.mcp.tokens.revoked'));
    if (c.kind === 'plugin') return run(`plugin:${c.id}`, () => axios.delete(`${ACCOUNT_API_BASE}/workspace/site-tokens/${encodeURIComponent(c.id)}`, { headers: authHeaders() }), t('userProfile.mcp.plugins.revoked'));
    return undefined;
  };

  if (loading) {
    return <div className="animate-pulse space-y-4"><div className="h-8 w-1/4 rounded bg-gray-200" /><div className="h-32 rounded bg-gray-200" /><div className="h-64 rounded bg-gray-200" /></div>;
  }
  if (error && !data) return <Alert variant="error" message={error} />;

  const links = issued ? installLinks(issued.mcp_url, issued.token) : null;
  const envLabel = (env) => t(`userProfile.mcp.env.${env}`, { defaultValue: env });

  return (
    <div className="space-y-12 pb-12">
      <div>
        <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.mcp.title')}</h2>
        <p className="mt-4 max-w-3xl text-sm leading-6 text-gray-600">{t('userProfile.mcp.description')}</p>
        <div className="mt-6 max-w-3xl">
          <CopyField value={data.mcp_url} label={t('userProfile.mcp.address')} t={t} />
          <p className="mt-2 text-sm text-gray-500">
            <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline">{t('userProfile.mcp.docs')}</a>
          </p>
        </div>
      </div>

      {notice && <Alert variant={notice.type} message={notice.text} />}

      {/* Sites */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('userProfile.mcp.sites.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('userProfile.mcp.sites.description')}</p>
          </div>
          <button type="button" className={btnGhost} onClick={() => setSiteForm((f) => ({ ...f, open: !f.open }))}>{t('userProfile.mcp.sites.connect')}</button>
        </div>
        {siteForm.open && (
          <div className={`${card} mt-4 space-y-4 p-6`}>
            <p className="text-sm text-gray-600">{t('userProfile.mcp.sites.connectHint')}</p>
            <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
              <input type="url" placeholder="https://example.com" value={siteForm.url} onChange={(e) => setSiteForm((f) => ({ ...f, url: e.target.value }))} className="form-input-inset" />
              <select value={siteForm.env} onChange={(e) => setSiteForm((f) => ({ ...f, env: e.target.value }))} className="form-input-inset">
                <option value="production">{envLabel('production')}</option>
                <option value="staging">{envLabel('staging')}</option>
              </select>
            </div>
            <TermsCheckbox id="mcp-site-terms" terms={data.terms} checked={siteForm.accept} onChange={(accept) => setSiteForm((f) => ({ ...f, accept }))} t={t} />
            <button type="button" className={btnPrimary} disabled={!siteForm.accept || !/^https:\/\/.+/.test(siteForm.url.trim()) || busy === 'site'} onClick={connectSite}>{t('userProfile.mcp.sites.continue')}</button>
          </div>
        )}
        <div className={`${card} mt-4`}>
          {data.sites.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">{t('userProfile.mcp.sites.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                <th className={th}>{t('userProfile.mcp.sites.name')}</th>
                <th className={th}>{t('userProfile.mcp.sites.env')}</th>
                <th className={th}>{t('userProfile.mcp.sites.user')}</th>
                <th className={th}>{t('userProfile.mcp.lastUsed')}</th>
                <th className={th}><span className="sr-only">{t('userProfile.mcp.actions')}</span></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {data.sites.map((s) => (
                  <tr key={s.name}>
                    <td className={td}><span className="font-medium text-gray-900">{s.name}</span><br /><span className="text-gray-500">{String(s.url).replace(/^https:\/\//, '')}</span></td>
                    <td className={td}><span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${s.env === 'production' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>{envLabel(s.env)}</span>{s.readOnly && <span className="ml-2 text-xs text-gray-500">{t('userProfile.mcp.sites.readOnly')}</span>}</td>
                    <td className={td}>{s.user}</td>
                    <td className={td}>{fmt(s.lastUsedAt)}</td>
                    <td className={`${td} text-right`}><button type="button" className={btnDanger} disabled={busy === `site:${s.name}`} onClick={() => setConfirm({ kind: 'site', id: s.name })}>{t('userProfile.mcp.sites.disconnect')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* MCP tokens */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('userProfile.mcp.tokens.title')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('userProfile.mcp.tokens.description')}</p>
          </div>
          <button type="button" className={btnPrimary} onClick={() => setTokenForm((f) => ({ ...f, open: !f.open }))}>{t('userProfile.mcp.tokens.new')}</button>
        </div>
        {tokenForm.open && (
          <div className={`${card} mt-4 space-y-4 p-6`}>
            <label className="block text-sm font-medium text-gray-900" htmlFor="mcp-token-label">{t('userProfile.mcp.tokens.label')}</label>
            <input id="mcp-token-label" type="text" maxLength={120} placeholder={t('userProfile.mcp.tokens.labelPlaceholder')} value={tokenForm.label} onChange={(e) => setTokenForm((f) => ({ ...f, label: e.target.value }))} className="form-input-inset" />
            <TermsCheckbox id="mcp-token-terms" terms={data.terms} checked={tokenForm.accept} onChange={(accept) => setTokenForm((f) => ({ ...f, accept }))} t={t} />
            <button type="button" className={btnPrimary} disabled={!tokenForm.accept || busy === 'token'} onClick={createToken}>{t('userProfile.mcp.tokens.create')}</button>
          </div>
        )}
        <div className={`${card} mt-4`}>
          {data.tokens.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">{t('userProfile.mcp.tokens.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                <th className={th}>{t('userProfile.mcp.tokens.label')}</th>
                <th className={th}>{t('userProfile.mcp.tokens.createdBy')}</th>
                <th className={th}>{t('userProfile.mcp.tokens.created')}</th>
                <th className={th}>{t('userProfile.mcp.lastUsed')}</th>
                <th className={th}><span className="sr-only">{t('userProfile.mcp.actions')}</span></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {data.tokens.map((k) => (
                  <tr key={k.id}>
                    <td className={`${td} font-medium text-gray-900`}>{k.label}</td>
                    <td className={td}>{k.createdBy || '—'}</td>
                    <td className={td}>{fmt(k.createdAt)}</td>
                    <td className={td}>{fmt(k.lastUsedAt)}</td>
                    <td className={`${td} text-right`}><button type="button" className={btnDanger} disabled={busy === `token:${k.id}`} onClick={() => setConfirm({ kind: 'token', id: k.id })}>{t('userProfile.mcp.tokens.revoke')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Plugins */}
      <section>
        <h3 className="text-lg font-medium text-gray-900">{t('userProfile.mcp.plugins.title')}</h3>
        <p className="mt-1 text-sm text-gray-600">{t('userProfile.mcp.plugins.description')}</p>
        <div className={`${card} mt-4`}>
          {plugins.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">{t('userProfile.mcp.plugins.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                <th className={th}>{t('userProfile.mcp.plugins.site')}</th>
                <th className={th}>{t('userProfile.mcp.plugins.connected')}</th>
                <th className={th}>{t('userProfile.mcp.lastUsed')}</th>
                <th className={th}><span className="sr-only">{t('userProfile.mcp.actions')}</span></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {plugins.map((p) => (
                  <tr key={p.id}>
                    <td className={`${td} font-medium text-gray-900`}>{String(p.site_url).replace(/^https?:\/\//, '')}</td>
                    <td className={td}>{fmt(p.created_at)}</td>
                    <td className={td}>{fmt(p.last_used_at)}</td>
                    <td className={`${td} text-right`}><button type="button" className={btnDanger} disabled={busy === `plugin:${p.id}`} onClick={() => setConfirm({ kind: 'plugin', id: p.id })}>{t('userProfile.mcp.plugins.revoke')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Log */}
      <section>
        <h3 className="text-lg font-medium text-gray-900">{t('userProfile.mcp.events.title')}</h3>
        <p className="mt-1 text-sm text-gray-600">{t('userProfile.mcp.events.description')}</p>
        <div className={`${card} mt-4`}>
          {data.events.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">{t('userProfile.mcp.events.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                <th className={th}>{t('userProfile.mcp.events.at')}</th>
                <th className={th}>{t('userProfile.mcp.sites.name')}</th>
                <th className={th}>{t('userProfile.mcp.events.tool')}</th>
                <th className={th}>{t('userProfile.mcp.events.result')}</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {data.events.map((e, i) => (
                  <tr key={`${e.at}-${i}`}>
                    <td className={`${td} whitespace-nowrap`}>{fmt(e.at)}</td>
                    <td className={td}>{e.site || '—'}</td>
                    <td className={`${td} font-mono text-[13px]`}>{e.tool}</td>
                    <td className={td}><span className={e.ok ? 'text-green-700' : 'text-red-600'}>{e.ok ? '✓' : '✕'}</span> {e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* New token, shown once */}
      <BaseModal isOpen={Boolean(issued)} onClose={() => setIssued(null)} size="lg">
        {issued && (
          <div className="space-y-6 bg-white p-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{t('userProfile.mcp.issued.title', { label: issued.label })}</h4>
              <p className="mt-2 text-sm text-gray-600">{t('userProfile.mcp.issued.once')}</p>
            </div>
            <CopyField value={issued.token} label={t('userProfile.mcp.issued.token')} t={t} />
            <div>
              <p className="text-sm font-medium text-gray-900">{t('userProfile.mcp.issued.oneClick')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <a href={links.cursor} className={btnPrimary}>{t('userProfile.mcp.issued.cursor')}</a>
                <a href={links.vscode} className={btnPrimary}>{t('userProfile.mcp.issued.vscode')}</a>
              </div>
            </div>
            <CopyField value={links.claudeCode} label={t('userProfile.mcp.issued.claudeCode')} t={t} />
            <p className="text-sm text-gray-600">
              {t('userProfile.mcp.issued.others')}{' '}
              <a href="https://prosecaptain.com/dokumentacja/mcp-klienci/" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline">{t('userProfile.mcp.issued.othersLink')}</a>
            </p>
            <div className="flex justify-end"><button type="button" className={btnGhost} onClick={() => setIssued(null)}>{t('userProfile.mcp.issued.done')}</button></div>
          </div>
        )}
      </BaseModal>

      {/* Confirmations */}
      <BaseModal isOpen={Boolean(confirm)} onClose={() => setConfirm(null)} size="md">
        {confirm && (
          <div className="space-y-4 bg-white p-6">
            <h4 className="text-base font-semibold text-gray-900">{t(`userProfile.mcp.confirm.${confirm.kind}.title`)}</h4>
            <p className="text-sm text-gray-600">{t(`userProfile.mcp.confirm.${confirm.kind}.text`, { name: confirm.id })}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setConfirm(null)}>{t('userProfile.mcp.confirm.cancel')}</button>
              <button type="button" className="inline-flex h-10 items-center rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-500" onClick={doConfirm}>{t(`userProfile.mcp.confirm.${confirm.kind}.ok`)}</button>
            </div>
          </div>
        )}
      </BaseModal>
    </div>
  );
};

export default McpTab;
